import React, { useRef } from 'react';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import StorageMentionPicker, { buildStorageReference } from './StorageMentionPicker';

const appendReference = (references, nextReference) => {
  const current = Array.isArray(references) ? references : [];
  if (current.some((reference) => reference?.path === nextReference.path)) {
    return current;
  }
  return [...current, nextReference];
};

const StorageMentionInput = ({
  value = '',
  onChange,
  storageRefs = [],
  onStorageRefsChange,
  disabled = false,
  ...textFieldProps
}) => {
  const { t } = useTranslation();
  const pickerRef = useRef(null);
  const inputRef = useRef(null);
  const mentionRangeRef = useRef(null);

  const openPickerAtCursor = (start, end) => {
    mentionRangeRef.current = { start, end };
    pickerRef.current?.open();
  };

  const handleChange = (event) => {
    const nextValue = event.target.value;
    const cursor = event.target.selectionStart ?? nextValue.length;
    onChange?.(nextValue);
    if (nextValue[cursor - 1] === '@') {
      openPickerAtCursor(cursor - 1, cursor);
    }
  };

  const handleSelect = (reference) => {
    const normalizedReference = buildStorageReference(reference.path);
    const currentValue = String(value || '');
    const range = mentionRangeRef.current;
    const start = range && currentValue.slice(range.start, range.end) === '@'
      ? range.start
      : (inputRef.current?.selectionStart ?? currentValue.length);
    const end = range && currentValue.slice(range.start, range.end) === '@'
      ? range.end
      : start;
    const insertedValue = `@${normalizedReference.label} `;
    const nextValue = `${currentValue.slice(0, start)}${insertedValue}${currentValue.slice(end)}`;
    onChange?.(nextValue);
    onStorageRefsChange?.((currentReferences) => (
      appendReference(currentReferences, normalizedReference)
    ));
    mentionRangeRef.current = null;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      const nextCursor = start + insertedValue.length;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handleRemove = (path) => {
    onStorageRefsChange?.((currentReferences) => (
      (Array.isArray(currentReferences) ? currentReferences : [])
        .filter((reference) => reference?.path !== path)
    ));
  };

  return (
    <>
      <TextField
        {...textFieldProps}
        fullWidth
        value={value}
        onChange={handleChange}
        inputRef={inputRef}
        disabled={disabled}
        helperText={textFieldProps.helperText || t('storage_mention.helper')}
      />
      <StorageMentionPicker
        ref={pickerRef}
        selectedRefs={storageRefs}
        onSelect={handleSelect}
        onRemove={handleRemove}
        disabled={disabled}
        compact
      />
    </>
  );
};

export default StorageMentionInput;
